import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  WEB_PERMISSION_MODULES,
  emptyPermissionsState,
  normalizePermissionsState,
} from "../../constants/webPermissions";
import {
  createWebMemberCall,
  deleteWebMemberCall,
  listWebMembersCall,
  updateWebMemberPermissionsCall,
  updateWebMemberStatusCall,
} from "../../redux/api/members.api";

const ROLE_LABELS = {
  admin: "Admin",
  university_admin: "University admin",
  custom_user: "Custom user",
};

function PermissionsEditor({ value, onChange, disabled = false }) {
  const perms = normalizePermissionsState(value);

  const toggle = (moduleKey, actionKey) => {
    if (disabled) return;
    onChange({
      ...perms,
      [moduleKey]: {
        ...perms[moduleKey],
        [actionKey]: !perms[moduleKey][actionKey],
      },
    });
  };

  return (
    <Stack spacing={1.25}>
      {Object.entries(WEB_PERMISSION_MODULES).map(([moduleKey, mod]) => (
        <Box
          key={moduleKey}
          sx={{
            p: 1.25,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {mod.label}
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={0}>
            {Object.entries(mod.actions).map(([actionKey, label]) => (
              <FormControlLabel
                key={`${moduleKey}.${actionKey}`}
                control={
                  <Checkbox
                    size="small"
                    checked={Boolean(perms[moduleKey]?.[actionKey])}
                    onChange={() => toggle(moduleKey, actionKey)}
                    disabled={disabled}
                  />
                }
                label={<Typography variant="caption">{label}</Typography>}
              />
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

export default function MembersPage() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [permOpen, setPermOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
  });
  const [draftPermissions, setDraftPermissions] = useState(emptyPermissionsState());

  const loadMembers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await listWebMembersCall();
      setMembers(res?.members || []);
    } catch (e) {
      setError(e?.message || "Failed to load members");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const openPermissions = (member) => {
    setSelected(member);
    setDraftPermissions(normalizePermissionsState(member.permissions));
    setPermOpen(true);
  };

  const handleCreate = async () => {
    setLoading(true);
    setError("");
    try {
      await createWebMemberCall({
        ...form,
        permissions: draftPermissions,
      });
      setCreateOpen(false);
      setForm({ first_name: "", last_name: "", email: "", password: "" });
      setDraftPermissions(emptyPermissionsState());
      await loadMembers();
    } catch (e) {
      setError(e?.message || "Could not create member");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!selected?.id) return;
    setLoading(true);
    setError("");
    try {
      await updateWebMemberPermissionsCall(selected.id, {
        permissions: draftPermissions,
      });
      setPermOpen(false);
      setSelected(null);
      await loadMembers();
    } catch (e) {
      setError(e?.message || "Could not save permissions");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (member) => {
    setLoading(true);
    try {
      await updateWebMemberStatusCall(member.id, {
        status: member.status === "active" ? "inactive" : "active",
      });
      await loadMembers();
    } catch (e) {
      setError(e?.message || "Could not update status");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (member) => {
    if (!window.confirm(`Delete custom user ${member.email}?`)) return;
    setLoading(true);
    try {
      await deleteWebMemberCall(member.id);
      await loadMembers();
    } catch (e) {
      setError(e?.message || "Could not delete member");
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <MDTypography variant="h5" fontWeight="medium">
            Members
          </MDTypography>
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={() => {
              setDraftPermissions(emptyPermissionsState());
              setCreateOpen(true);
            }}
          >
            <PersonAddIcon sx={{ mr: 0.5, fontSize: 18 }} />
            Add custom user
          </MDButton>
        </MDBox>

        <Typography variant="body2" color="text.secondary" mb={2}>
          Platform admins, university admins, and custom users with module permissions.
          Only custom users have editable access checkboxes.
        </Typography>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Card>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>University</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.id} hover>
                    <TableCell>
                      {m.first_name} {m.last_name}
                    </TableCell>
                    <TableCell>{m.email}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={ROLE_LABELS[m.role] || m.role}
                        color={
                          m.role === "admin"
                            ? "error"
                            : m.role === "university_admin"
                              ? "warning"
                              : "info"
                        }
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{m.university_name || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={m.status || "active"}
                        color={m.status === "inactive" ? "default" : "success"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {m.role === "custom_user" ? (
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Module permissions">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => openPermissions(m)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <MDButton
                            variant="outlined"
                            color={m.status === "inactive" ? "success" : "warning"}
                            size="small"
                            onClick={() => handleToggleStatus(m)}
                            disabled={loading}
                          >
                            {m.status === "inactive" ? "Enable" : "Disable"}
                          </MDButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(m)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Full access
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {!members.length && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        No members found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </MDBox>

      <Dialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: "90vh" } }}
      >
        <DialogTitle>Add custom user</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="First name"
                fullWidth
                size="small"
                value={form.first_name}
                onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
              />
              <TextField
                label="Last name"
                fullWidth
                size="small"
                value={form.last_name}
                onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
              />
            </Stack>
            <TextField
              label="Email"
              type="email"
              fullWidth
              size="small"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              size="small"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              helperText="Minimum 8 characters"
            />
            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>
              Module permissions
            </Typography>
            <PermissionsEditor value={draftPermissions} onChange={setDraftPermissions} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setCreateOpen(false)}>
            Cancel
          </MDButton>
          <MDButton variant="gradient" color="info" onClick={handleCreate} disabled={loading}>
            Create user
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={permOpen}
        onClose={() => setPermOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        PaperProps={{ sx: { maxHeight: "90vh" } }}
      >
        <DialogTitle>
          Permissions — {selected?.first_name} {selected?.last_name}
        </DialogTitle>
        <DialogContent dividers>
          <PermissionsEditor value={draftPermissions} onChange={setDraftPermissions} />
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setPermOpen(false)}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleSavePermissions}
            disabled={loading}
          >
            Save permissions
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
